import { ID, Task, UpdateTask } from '@griply/types';

import { buildTaskLinkWrites, TaskLinkWrites } from './build-task-link-writes';

export type BuildTaskUpdateWithLinksOptions = {
  task: Task;
  update: UpdateTask;
};

const linkFields = ['goalId', 'goalIds', 'lifeAreaId', 'lifeAreaIds'] as const;

// A scalar-only write is silently ignored by readers once the array exists, so the scalar is widened to the array here.
const resolveDesiredIds = (
  ids: ID[] | undefined,
  legacyId: ID | null | undefined,
  currentIds: ID[],
): ID[] => {
  if (ids !== undefined) {
    return ids;
  }

  if (legacyId === undefined) {
    return currentIds;
  }

  return legacyId ? [legacyId] : [];
};

const withoutLinkFields = (update: UpdateTask): UpdateTask =>
  Object.fromEntries(
    Object.entries(update).filter(
      ([key]) => !linkFields.includes(key as (typeof linkFields)[number]),
    ),
  ) as UpdateTask;

export const buildTaskUpdateWithLinks = ({
  task,
  update,
}: BuildTaskUpdateWithLinksOptions): TaskLinkWrites => {
  const currentGoalIds = task.goalIds ?? [];
  const currentLifeAreaIds = task.lifeAreaIds ?? [];

  const linkWrites = buildTaskLinkWrites({
    taskId: task.id,
    goalIds: resolveDesiredIds(update.goalIds, update.goalId, currentGoalIds),
    lifeAreaIds: resolveDesiredIds(
      update.lifeAreaIds,
      update.lifeAreaId,
      currentLifeAreaIds,
    ),
    currentGoalIds,
    currentLifeAreaIds,
    isCompleted: !!task.completedAt,
  });

  if (!linkWrites.taskUpdate) {
    const rest = withoutLinkFields(update);

    return {
      ...linkWrites,
      taskUpdate: Object.keys(rest).length > 1 ? rest : null,
    };
  }

  return {
    ...linkWrites,
    // Spreading two UpdateTasks widens `type` across the union; the link fields all sit on TaskBase, so the variant is unchanged.
    taskUpdate: { ...update, ...linkWrites.taskUpdate } as UpdateTask,
  };
};
